// The secondary lines on the Nodes and Networks tabs (app#350): how many nodes a network has, how
// many of them are online, and for how long a node has been offline.
//
// ⚠ This module imports nothing from `models/`. `useNode` pulls in the Supabase client, which a
// `node --experimental-strip-types` test cannot load. The offline rule is here, in ONE place, and
// `models/useNode.ts` exports it too. `countByNetwork` takes a rule, because its caller counts rows
// that hold a node.

/** A node that never connected is offline too. */
export function isNodeOffline(node: { onlineAt?: string | null; offlineAt?: string | null }): boolean {
  return !node.onlineAt || !!node.offlineAt
}

export type NodeCount = { total: number; online: number }

type CountableNode = { networkIds: readonly string[] }

// Counts per network id. A node counts once in EACH network it is in: the count of a section is for
// that section (app#498), and the app states no total. A node in no network is in no count here.
export const countByNetwork = <N extends CountableNode>(
  nodes: readonly N[],
  isOffline: (node: N) => boolean
): Map<string, NodeCount> => {
  const counts = new Map<string, NodeCount>()
  for (const node of nodes) {
    for (const networkId of node.networkIds) {
      const count = counts.get(networkId) ?? { total: 0, online: 0 }
      count.total += 1
      if (!isOffline(node)) count.online += 1
      counts.set(networkId, count)
    }
  }
  return counts
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

// "4 nodes · 3 online", "1 node · none online", "No nodes".
//
// ⚠⚠ `complete` IS FALSE WHILE OFFLINE NODES ARE HIDDEN, AND THEN ONLY THE ONLINE COUNT IS PRINTED.
// With `hideOffline` on (the default; the side menu button then reads "Show Offline"), the nodes
// query drops every node that has been offline for more than OFFLINE_NODE_VISIBILITY_HOURS. The loaded TOTAL is then too low,
// and a network whose nodes are all long offline would read "No nodes", which is false. The ONLINE
// count stays exact, because an online node has no `offline_at` and the query always keeps it.
// So we print only the number we can stand behind.
export const countLabel = (count: NodeCount | undefined, complete: boolean): string => {
  const total = count?.total ?? 0
  const online = count?.online ?? 0
  if (!complete) return online === 0 ? 'None online' : `${online} online`
  if (total === 0) return 'No nodes'
  return `${plural(total, 'node', 'nodes')} · ${online === 0 ? 'none' : online} online`
}

const MINUTE = 60_000

// "Online", "Offline for 5 min", "Offline for 3 h", "Offline since 2 Sep".
//
// ⚠⚠ THIS MUST NEVER THROW. The app has no error boundary, so one bad `offline_at` on one row
// would unmount the whole drawer. An absent or unparseable stamp reads as plain "Offline".
//
// ⚠ A stamp in the FUTURE is a clock difference between this browser and the database. It reads
// as "under 1 min" and not as a negative age.
//
// ⚠ Past a day, the DATE and not "Offline for 26 h": the date answers "since when" without
// arithmetic. It is in the reader's zone, because it is an instant and not a billing boundary
// (see `freshnessLabel` in `freshness.ts`). The year is printed only when it is not this year.
export const offlineLabel = (
  offline: boolean,
  offlineAt: string | null | undefined,
  now: number = Date.now()
): string => {
  if (!offline) return 'Online'
  if (!offlineAt) return 'Offline'
  const t = new Date(offlineAt).getTime()
  if (Number.isNaN(t)) return 'Offline'
  const minutes = Math.floor((now - t) / MINUTE)
  if (minutes < 1) return 'Offline for under 1 min'
  if (minutes < 60) return `Offline for ${minutes} min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `Offline for ${hours} h`
  const d = new Date(t)
  const sameYear = d.getFullYear() === new Date(now).getFullYear()
  const date = d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  })
  return `Offline since ${date}`
}
